'use client'

import React, { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2, Search, X, Copy, Check, RefreshCw } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { compactDate, watchClasses } from './ProcessedEntry/DomainEntry/processedTypes'
import { Widget } from './Widget'

export type PipelineStepInput = {
  step: string
  jsonInfo?: Record<string, unknown> | null
  json_info?: Record<string, unknown> | null
  createdAt?: string
  created_at?: string
}

type NormalizedStep = {
  step: string
  jsonInfo: Record<string, unknown> | null
  createdAt: string | null
}

export type Tone = 'success' | 'error' | 'active' | 'neutral'

export const STEP_META: Record<string, { label: string; meaning: string; tone: Tone }> = {
  classify: {
    label: 'Classification started',
    meaning: 'The entry was queued for automated scam classification.',
    tone: 'active',
  },
  classify_success: {
    label: 'Classification completed',
    meaning: 'The classifier completed and stored its verdict, claims, score, and user-facing explanation.',
    tone: 'success',
  },
  classify_retry_success: {
    label: 'Classification retry completed',
    meaning: 'A retry of the classifier completed and produced a replacement classification payload.',
    tone: 'success',
  },
  classify_failed: {
    label: 'Classification failed',
    meaning: 'The classifier returned an error or could not produce a valid result.',
    tone: 'error',
  },
  pipeline_start: {
    label: 'Pipeline started',
    meaning: 'Processing began for this entry.',
    tone: 'active',
  },
  pipeline_end: {
    label: 'Pipeline completed',
    meaning: 'Processing finished for this entry.',
    tone: 'success',
  },
  field_correction: {
    label: 'Field correction',
    meaning: 'An operator or automated rule changed one or more extracted fields.',
    tone: 'neutral',
  },
}

// json_info is declared as an already-parsed object, but that's not always
// true in practice — agentic/LLM trace output frequently nests a JSON-encoded
// string *inside* an otherwise-parsed object (a tool call's raw response, a
// trace step's `output`, ...), sometimes more than one layer deep. A single
// top-level JSON.parse only unwraps the outermost layer; any string field
// anywhere in the tree that is itself JSON text stays opaque to both direct
// property reads (ClassificationDetails/TraceOutputDetails) and to search.
// This recursively unwraps every such layer instead of just the first one.
function deepParseJson(value: unknown, depth = 0): unknown {
  if (value == null || depth > 8) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    const looksLikeJson = (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))
    if (!looksLikeJson) return value
    try {
      return deepParseJson(JSON.parse(trimmed), depth + 1)
    } catch {
      return value
    }
  }
  if (Array.isArray(value)) return value.map((item) => deepParseJson(item, depth + 1))
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) out[key] = deepParseJson(v, depth + 1)
    return out
  }
  return value
}

function normalizeStep(step: PipelineStepInput): NormalizedStep {
  const raw = step.jsonInfo ?? step.json_info ?? null
  return {
    step: step.step,
    jsonInfo: (raw != null ? deepParseJson(raw) : null) as Record<string, unknown> | null,
    createdAt: step.createdAt ?? step.created_at ?? null,
  }
}

export function stepMeta(step: string) {
  if (STEP_META[step]) return STEP_META[step]
  if (step.includes('failed') || step.includes('error')) {
    return { label: humanizeStep(step), meaning: 'This step indicates a failed or errored pipeline action.', tone: 'error' as const }
  }
  // 'finished' — e.g. "manual_probe_domain finished", which otherwise read as
  // neutral, the same as the "started" row a lost run stops at.
  if (step.includes('success') || step.includes('complete') || step.includes('finished') || step.includes('end')) {
    return { label: humanizeStep(step), meaning: 'This step indicates a completed pipeline action.', tone: 'success' as const }
  }
  return { label: humanizeStep(step), meaning: 'A recorded pipeline action. Expand the payload for step-specific details.', tone: 'neutral' as const }
}

export function humanizeStep(step: string) {
  return step
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

// Friendly labels for step_name values inside a classify step's
// json_info.pipeline_trace (the classify_e34 agentic trace — see
// TraceStepOutput below for the field-level specialization). Falls back to
// humanizeStep for anything not listed here (tool steps get added to this
// list over time; an unlabeled one still renders fine, just less pretty).
const TRACE_STEP_META: Record<string, string> = {
  text_intake: 'Text intake',
  eml_parse: 'Email parsed',
  image_extraction: 'Image OCR',
  attribute: 'Attribution',
  routing: 'Routing',
  get_domain_info: 'Domain lookup',
  get_bk_referencia: 'Multibanco reference lookup',
  get_entities_by_name: 'Entity lookup',
  analyze_domain_whois_result: 'WHOIS analysis',
  analyze_entity_data: 'Entity analysis',
  attribute_target: 'Target attribution',
  get_similar_cases: 'Similar cases search',
  no_signals_assessment: 'No-signals assessment',
  deliberation: 'Deliberation',
  translation: 'Translation',
  actionable_evidence: 'Actionable evidence',
  // a promoted auto-domain candidate classified from its own pass
  // (libs/tooling/candidate_classification): one entry per analysis row
  network: 'Network probe',
  whois: 'WHOIS (candidate)',
  http: 'HTTP fetch',
  browser: 'Browser capture',
  content: 'Screenshot analysis (e33)',
  classify: 'Heuristic verdict',
}

function traceStepLabel(stepName: string | null) {
  if (!stepName) return null
  return TRACE_STEP_META[stepName] ?? humanizeStep(stepName)
}

// ─── Generic collapsible key/value tree ────────────────────────────────────
// Replaces a previous generation of this file that had one bespoke,
// hand-labeled component per step_name — that hid the actual JSON keys
// behind invented labels and always rendered every nested array/object
// fully expanded. This instead renders every key exactly as it's named in
// the payload, and any non-empty object or array of objects starts
// collapsed — click to drill into it, rather than everything dumped open at
// once. Arrays of plain strings/numbers render inline (nothing to drill
// into there), and object.

function Pill({ children, tone, theme }: { children: React.ReactNode; tone: Tone; theme: ProcessedTheme }) {
  return <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${toneClasses(theme, tone)}`}>{children}</span>
}

function BoolPill({ value, theme, trueLabel = 'true', falseLabel = 'false' }: { value: boolean; theme: ProcessedTheme; trueLabel?: string; falseLabel?: string }) {
  return <Pill tone={value ? 'success' : 'neutral'} theme={theme}>{value ? trueLabel : falseLabel}</Pill>
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

// A leaf is anything JsonTreeRow can render inline on its own line without a
// disclosure toggle: primitives, null/empty, and arrays that are themselves
// entirely primitives (nothing to drill into further).
function isLeafValue(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return true
  if (Array.isArray(value)) return value.length === 0 || value.every((v) => v === null || typeof v !== 'object')
  return Object.keys(value).length === 0
}

function jsonTextIncludes(value: unknown, query: string): boolean {
  if (!query) return false
  try {
    return JSON.stringify(value).toLowerCase().includes(query)
  } catch {
    return false
  }
}

function JsonLeaf({ value, theme, query = '' }: { value: unknown; theme: ProcessedTheme; query?: string }) {
  const c = watchClasses(theme)
  const highlightCls = 'bg-signal-surface text-signal'
  if (value === null || value === undefined) return <span className={c.faint}>—</span>
  if (typeof value === 'boolean') return <BoolPill value={value} theme={theme} />
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className={c.faint}>[]</span>
    return (
      <span className="flex flex-wrap gap-1.5">
        {value.map((item, i) => {
          const text = String(item)
          const match = query.length > 0 && text.toLowerCase().includes(query)
          return <span key={i} className={`rounded border px-1.5 py-0.5 font-mono text-[10px] ${c.line} ${match ? highlightCls : `${c.soft} ${c.text}`}`}>{text}</span>
        })}
      </span>
    )
  }
  if (isPlainObject(value)) return <span className={c.faint}>{'{}'}</span>
  const text = String(value)
  const match = query.length > 0 && text.toLowerCase().includes(query)
  return <span className={`break-words rounded px-0.5 font-mono text-[11px] ${match ? highlightCls : c.text}`}>{text}</span>
}

// One key's row: a leaf renders inline immediately, a non-empty object/
// object-array renders as a closed disclosure (count badge) the user opens
// on click — collapsed by default, recursively, so a deeply nested step
// (get_similar_cases' cases, an attribute-shaped sub-object, ...) never
// dumps everything on screen at once.
//
// query (lowercased/trimmed by the caller) makes search reach inside that
// collapsed content instead of only matching whatever happens to already
// be on screen: a row whose key or any nested value contains it forces
// itself open, and the actual matching leaf text gets highlighted, so
// searching "surfaces" a hit buried several levels deep in a step's output
// the same way it already highlights a whole matching step card.
// stepDescriptions is only ever read for the one key this recurses into
// that needs it (pipeline_trace, below) — every other row ignores it and
// just passes it along in case a deeper array-of-objects turns out to
// contain a nested pipeline_trace of its own.
function JsonTreeRow({ k, value, theme, query = '', stepDescriptions }: { k: string; value: unknown; theme: ProcessedTheme; query?: string; stepDescriptions?: Record<string, unknown> | null }) {
  const c = watchClasses(theme)
  const [manualOpen, setManualOpen] = useState(false)
  const keyMatches = query.length > 0 && k.toLowerCase().includes(query)
  const contentMatches = query.length > 0 && jsonTextIncludes(value, query)

  if (isLeafValue(value)) {
    return (
      <div className={`flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-0.5 ${keyMatches ? 'ring-1 ring-ring rounded px-1' : ''}`}>
        <span className={`shrink-0 font-mono text-[9px] uppercase tracking-widest ${keyMatches ? 'text-signal' : c.faint}`}>{k}</span>
        <JsonLeaf value={value} theme={theme} query={query} />
      </div>
    )
  }

  const isArray = Array.isArray(value)
  const count = isArray ? value.length : Object.keys(value as object).length
  const open = manualOpen || contentMatches
  // pipeline_trace gets richer content once opened (step_name/status/
  // duration/description per entry, plus each entry's own output/input
  // tree) — but the row itself is exactly the same closed-by-default
  // disclosure every other key gets, at whatever position it naturally
  // falls in the payload, not pulled out and led with above everything else.
  const isPipelineTrace = isArray && (k === 'pipeline_trace' || k === 'pipelineTrace')

  return (
    <div className="py-0.5">
      <button
        type="button"
        onClick={() => setManualOpen((v) => !v)}
        className={`flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest transition-colors hover:text-signal ${keyMatches ? 'text-signal' : c.faint}`}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {k}
        <span className={`rounded border px-1 ${c.line} ${c.faint}`}>{isArray ? `${count} item${count === 1 ? '' : 's'}` : `${count} key${count === 1 ? '' : 's'}`}</span>
        {contentMatches && <span className="rounded border border-signal/30 px-1 text-signal">match</span>}
      </button>
      {open && (
        <div className={`mt-1 ml-4 space-y-1 border-l pl-3 ${c.line}`}>
          {isPipelineTrace ? (
            <PipelineTraceCards trace={value as unknown[]} stepDescriptions={stepDescriptions ?? null} theme={theme} query={query} />
          ) : isArray ? (
            (value as unknown[]).map((item, i) => (
              isPlainObject(item)
                ? <JsonTree key={i} data={item} theme={theme} wrapperLabel={`[${i}]`} query={query} stepDescriptions={stepDescriptions} />
                : <JsonTreeRow key={i} k={`[${i}]`} value={item} theme={theme} query={query} stepDescriptions={stepDescriptions} />
            ))
          ) : (
            Object.entries(value as Record<string, unknown>).map(([key, v]) => <JsonTreeRow key={key} k={key} value={v} theme={theme} query={query} stepDescriptions={stepDescriptions} />)
          )}
        </div>
      )}
    </div>
  )
}

// wrapperLabel is only set for an array-of-objects entry recursing into
// JsonTree for one of its items (JsonTreeRow above) — shown as a small
// index marker so it's clear which array position a nested object came from.
function JsonTree({ data, theme, wrapperLabel, query = '', stepDescriptions }: { data: Record<string, unknown> | null; theme: ProcessedTheme; wrapperLabel?: string; query?: string; stepDescriptions?: Record<string, unknown> | null }) {
  const c = watchClasses(theme)
  if (!data || Object.keys(data).length === 0) return null
  return (
    <div>
      {wrapperLabel && <div className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>{wrapperLabel}</div>}
      <div className={wrapperLabel ? 'ml-2 border-l pl-3' : ''}>
        {Object.entries(data).map(([key, value]) => <JsonTreeRow key={key} k={key} value={value} theme={theme} query={query} stepDescriptions={stepDescriptions} />)}
      </div>
    </div>
  )
}

// The rich per-step card list (step_name/status/duration/description, each
// entry's own output/input tree, its step_descriptions cross-reference when
// that differs from its own description) — same content as before, just
// triggered from inside pipeline_trace's own JsonTreeRow disclosure instead
// of being hoisted above the rest of the payload with its own header.
function PipelineTraceCards({
  trace,
  stepDescriptions,
  theme,
  query,
}: {
  trace: unknown[]
  stepDescriptions: Record<string, unknown> | null
  theme: ProcessedTheme
  query: string
}) {
  const c = watchClasses(theme)
  const entries = trace.map(asRecord).filter((item): item is Record<string, unknown> => item != null)

  if (entries.length === 0) {
    return (
      <div className={`rounded border px-3 py-2 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.faint}`}>
        No pipeline trace was recorded for this classification payload.
      </div>
    )
  }

  return (
    <ol className="space-y-2">
      {entries.map((traceEntry, index) => {
        const description = getString(traceEntry, 'description', 'description')
        // step_name/duration_ms are the actual snake_case keys the
        // classify_e34 trace uses — these used to look for a camelCase key
        // under both arguments, which never matched.
        const stepName = getString(traceEntry, 'step_name', 'stepName')
        const status = getString(traceEntry, 'status', 'status')
        const durationMs = getNumber(traceEntry, 'duration_ms', 'durationMs')
        const output = asRecord(traceEntry.output)
        const input = asRecord(traceEntry.input)
        // Only rendered when it actually adds information — for a step_name
        // that occurs once this is always identical to `description` above
        // (redundant to repeat), but for a repeating one (image_extraction)
        // step_descriptions only keeps the *last* occurrence's text, so an
        // earlier occurrence's card can legitimately differ from it.
        const stepDescriptionEntry = stepName && stepDescriptions ? stepDescriptions[stepName] : undefined
        const showStepDescription = typeof stepDescriptionEntry === 'string' && stepDescriptionEntry.trim() && stepDescriptionEntry !== description
        if (!description) return null
        // Which *substep* actually matched — searching a step surfaces the
        // whole card, but with several reasoning steps inside it's not
        // obvious which one had the hit without this.
        const isMatch = query.length > 0 && jsonTextIncludes(traceEntry, query)
        const isDimmed = query.length > 0 && !isMatch
        return (
          <li
            key={`${description}-${index}`}
            className={`relative rounded-md border px-4 py-3 pl-12 text-sm leading-6 transition-opacity ${c.line} ${theme === 'dark' ? 'bg-signal-surface text-foreground' : 'bg-signal-surface text-muted-foreground'} ${isMatch ? 'ring-1 ring-ring' : ''} ${isDimmed ? 'opacity-40' : ''}`}
          >
            <span className={`absolute left-3 top-3 flex h-6 w-6 items-center justify-center rounded-full border font-mono text-[10px] ${theme === 'dark' ? 'border-signal/30 bg-signal-surface text-signal' : 'border-signal/30 bg-card text-signal'}`}>
              {index + 1}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {stepName && (
                <span className={`font-mono text-[10px] uppercase tracking-widest ${c.text}`} title={stepName}>
                  {traceStepLabel(stepName)}
                </span>
              )}
              {status && <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${status === 'ok' ? 'border-success/40 text-success' : 'border-destructive/40 text-destructive'}`}>{status}</span>}
              {durationMs !== null && <span className={`font-mono text-[9px] ${c.faint}`}>{Math.round(durationMs)}ms</span>}
              {isMatch && <span className="rounded border border-signal/30 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-signal">match</span>}
            </div>
            <p className="mt-1">{description}</p>
            <TraceStepOutput output={output} theme={theme} query={query} />
            <TraceStepInput input={input} theme={theme} query={query} />
            {showStepDescription && (
              <div className="mt-2">
                <JsonTreeRow k="step_descriptions" value={stepDescriptionEntry} theme={theme} query={query} />
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

// A trace entry's `output` — every key exactly as named in the payload. The
// whole thing is itself one more closed-by-default node (reusing
// JsonTreeRow directly, the same disclosure every nested key inside it
// uses) rather than being force-expanded the moment the step card opens —
// opening a step used to dump every one of output's own top-level keys
// immediately, which is exactly the "everything already open" this fixes:
// now opening a step only reveals "▸ output · N keys", and every field
// (including plain leaves) needs its own click same as any other level.
function TraceStepOutput({ output, theme, query = '' }: { output: Record<string, unknown> | null; theme: ProcessedTheme; query?: string }) {
  if (!output || Object.keys(output).length === 0) return null
  return (
    <div className="mt-2">
      <JsonTreeRow k="output" value={output} theme={theme} query={query} />
    </div>
  )
}

// Same treatment for a trace entry's optional `input` (present on tool-
// call-shaped steps — _trace_step called with args).
function TraceStepInput({ input, theme, query = '' }: { input: Record<string, unknown> | null; theme: ProcessedTheme; query?: string }) {
  if (!input || Object.keys(input).length === 0) return null
  return (
    <div className="mt-2">
      <JsonTreeRow k="input" value={input} theme={theme} query={query} />
    </div>
  )
}

function isClassifySuccessStep(step: string) {
  return step === 'classify_success' ||
    step === 'classify_retry_success' ||
    step.startsWith('classify_manual_retry_success_') ||
    step.startsWith('classify_manual_update')
}

function toneClasses(theme: ProcessedTheme, tone: Tone) {
  const dark = theme === 'dark'
  if (tone === 'success') return dark
    ? 'border-success/40 bg-success-surface text-success'
    : 'border-success/40 bg-success-surface text-success'
  if (tone === 'error') return dark
    ? 'border-destructive/40 bg-danger-surface text-destructive'
    : 'border-destructive/40 bg-danger-surface text-destructive'
  if (tone === 'active') return dark
    ? 'border-signal/30 bg-signal-surface text-signal'
    : 'border-signal/30 bg-signal-surface text-signal'
  return dark
    ? 'border-border bg-secondary text-foreground'
    : 'border-border bg-secondary text-muted-foreground'
}

function summarizeJson(value: Record<string, unknown> | null) {
  if (!value || Object.keys(value).length === 0) return null

  const summary: string[] = []
  const traceCount = getPipelineTrace(value).length
  const claimCount = getStringArray(value, 'scam_claims', 'scamClaims').length
  if (traceCount > 0) summary.push(`${traceCount} trace step${traceCount === 1 ? '' : 's'}`)
  if (claimCount > 0) summary.push(`${claimCount} claim${claimCount === 1 ? '' : 's'}`)
  if (typeof value.isScam === 'boolean') summary.push(`isScam: ${value.isScam ? 'true' : 'false'}`)
  if (typeof value.is_scam === 'boolean') summary.push(`is_scam: ${value.is_scam ? 'true' : 'false'}`)
  if (typeof value.scamValue === 'number') summary.push(`score: ${value.scamValue}`)
  if (typeof value.scam_value === 'number') summary.push(`score: ${value.scam_value}`)
  if (Array.isArray(value.scamClaims)) summary.push(`${value.scamClaims.length} claims`)
  if (Array.isArray(value.scam_claims)) summary.push(`${value.scam_claims.length} claims`)

  const translation = value.translation
  if (translation && typeof translation === 'object' && !Array.isArray(translation)) {
    const verdict = (translation as Record<string, unknown>).verdictLabel ?? (translation as Record<string, unknown>).verdict_label
    if (typeof verdict === 'string') summary.push(verdict)
  }

  return summary.length > 0 ? summary.join(' · ') : `${Object.keys(value).length} payload keys`
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function getNumber(value: Record<string, unknown>, snakeKey: string, camelKey: string) {
  const raw = value[snakeKey] ?? value[camelKey]
  return typeof raw === 'number' ? raw : null
}

function getString(value: Record<string, unknown>, snakeKey: string, camelKey: string) {
  const raw = value[snakeKey] ?? value[camelKey]
  return typeof raw === 'string' && raw.trim() ? raw : null
}

function getStringArray(value: Record<string, unknown>, snakeKey: string, camelKey: string) {
  const raw = value[snakeKey] ?? value[camelKey]
  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string' && item.trim().length > 0) : []
}

function getPipelineTrace(value: Record<string, unknown>) {
  const raw = value.pipeline_trace ?? value.pipelineTrace
  return Array.isArray(raw)
    ? raw.map(asRecord).filter((item): item is Record<string, unknown> => item != null)
    : []
}

function ClassificationDetails({
  jsonInfo,
  theme,
  query = '',
}: {
  jsonInfo: Record<string, unknown>
  theme: ProcessedTheme
  query?: string
}) {
  const q = query.trim().toLowerCase()

  // step_descriptions is a flat {step_name: description} map — looked up
  // per step_name and cross-referenced onto that step's own trace card
  // (see PipelineTraceCards) rather than read out here directly, but
  // ClassificationDetails is where jsonInfo is in scope to find it, so it's
  // still computed here and threaded down through the tree.
  const stepDescriptions = asRecord(jsonInfo.step_descriptions ?? jsonInfo.stepDescriptions)

  // Every jsonInfo key — is_scam, pipeline_trace, translation, scam_claims,
  // all of it — through the one generic, closed-by-default key/value tree,
  // in whatever order they actually appear in the payload. No more
  // hand-picked "Verdict/Score/Visibility" summary cards or a "Pipeline
  // trace" section led with above everything else: pipeline_trace is a key
  // like any other now (JsonTreeRow special-cases only *how* it renders
  // once opened, not *where* it sits or *whether* it starts open).
  return <JsonTree data={jsonInfo} theme={theme} query={q} stepDescriptions={stepDescriptions} />
}

export function PipelineStepsWidget({
  steps,
  theme,
  label = 'Pipeline steps',
  className = '',
  maxHeightClassName = 'max-h-[520px]',
  // Optional — callers polling for steps that may still be in flight (e.g.
  // classification deferred server-side) can pass this so the header shows
  // "checking…" instead of a stale "0 recorded" that reads as "nothing ever
  // came back" while a fetch is actually still pending.
  loading = false,
  // Overridable rather than folding state into `label` — the header bar is
  // narrow (label + badge + expand button share one row) and has no room for
  // a long "Pipeline steps (waiting for a submission)"-style string; the body
  // has plenty of room for that instead.
  emptyMessage = 'No pipeline steps recorded',
  // Optional on-demand refetch — e.g. the workbench's post-submit poll gives
  // up after a fixed number of attempts, so this lets the operator ask for
  // the latest state directly instead of resubmitting.
  onRefresh,
  refreshing = false,
}: {
  steps: PipelineStepInput[] | undefined
  theme: ProcessedTheme
  label?: React.ReactNode
  className?: string
  maxHeightClassName?: string
  loading?: boolean
  emptyMessage?: string
  onRefresh?: () => void
  refreshing?: boolean
}) {
  const c = watchClasses(theme)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [query, setQuery] = useState('')
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [copiedAll, setCopiedAll] = useState(false)

  const handleCopyJson = (index: number, jsonInfo: Record<string, unknown> | null) => {
    navigator.clipboard?.writeText(JSON.stringify(jsonInfo, null, 2)).then(() => {
      setCopiedIndex(index)
      setTimeout(() => setCopiedIndex((cur) => (cur === index ? null : cur)), 1500)
    })
  }

  const normalized = useMemo(
    () =>
      (steps ?? [])
        .map(normalizeStep)
        .sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return bTime - aTime
        }),
    [steps],
  )

  const handleCopyAll = () => {
    const payload = normalized.map(({ step, jsonInfo, createdAt }) => ({ step, createdAt, jsonInfo }))
    navigator.clipboard?.writeText(JSON.stringify(payload, null, 2)).then(() => {
      setCopiedAll(true)
      setTimeout(() => setCopiedAll(false), 1500)
    })
  }

  // Matches against everything textual on a step — its name, the humanized
  // label/meaning, and every string anywhere in jsonInfo (JSON.stringify
  // captures nested trace descriptions/claims/tool args too, not just
  // top-level fields) — so "search all of the text that's in the steps"
  // really does cover the full payload, not just the visible summary line.
  const filteredEntries = useMemo(() => {
    const entries = normalized.map((item, index) => ({ item, index }))
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries.filter(({ item }) => {
      const meta = stepMeta(item.step)
      const haystack = `${item.step} ${meta.label} ${meta.meaning} ${item.jsonInfo ? JSON.stringify(item.jsonInfo) : ''}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [normalized, query])

  const toggle = (index: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(index) ? next.delete(index) : next.add(index)
      return next
    })

  return (
    <Widget
      label={label}
      theme={theme}
      className={`${maxHeightClassName} ${className}`}
      scrollable
      headerRight={
        (loading || normalized.length > 0 || onRefresh) ? (
          <div className="flex items-center gap-1.5">
            {loading ? (
              <span className={`flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>
                <Loader2 className="h-3 w-3 animate-spin" />
                checking…
              </span>
            ) : normalized.length > 0 ? (
              <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>
                {normalized.length} recorded
              </span>
            ) : null}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                title="Refresh"
                className={`flex items-center rounded border p-1 transition-colors hover:text-signal disabled:opacity-50 ${c.line} ${c.faint}`}
              >
                <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        ) : undefined
      }
    >
      {normalized.length === 0 ? (
        <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>{emptyMessage}</p>
      ) : (
        <div className="space-y-2">
          <div className={`sticky top-0 z-10 -mx-5 -mt-5 mb-2 flex items-center gap-2 px-5 pt-5 pb-2 ${c.panel}`}>
            <div className="relative flex-1 min-w-0">
              <Search className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${c.faint}`} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search step text and payloads…"
                className={`w-full rounded border py-1.5 pl-8 pr-8 text-[12px] ${c.input}`}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 hover:text-signal ${c.faint}`}
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleCopyAll}
              title="Copy every step's raw JSON as one array"
              className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal ${c.line} ${c.faint}`}
            >
              {copiedAll ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copiedAll ? 'Copied' : 'Copy all'}
            </button>
          </div>
          {filteredEntries.length === 0 && (
            <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No steps match &ldquo;{query}&rdquo;</p>
          )}
          {filteredEntries.map(({ item, index }) => {
            const meta = stepMeta(item.step)
            const hasJson = item.jsonInfo != null && Object.keys(item.jsonInfo).length > 0
            // Auto-expand while a search is active — filteredEntries already only
            // contains matching steps, and with the match often buried in a
            // substep's payload, requiring an extra click to even see it would
            // defeat the point of searching "inside" a step.
            const open = expanded.has(index) || query.trim().length > 0
            const jsonSummary = summarizeJson(item.jsonInfo)
            const isClassification = isClassifySuccessStep(item.step)

            return (
              <div key={`${item.step}-${item.createdAt ?? index}`} data-pipeline-step={(item as any).id ?? `${item.step}-${item.createdAt ?? index}`} className={`rounded-md border ${c.line} bg-card`}>
                <button
                  type="button"
                  className={`flex w-full items-start gap-3 px-3 py-3 text-left ${hasJson ? 'cursor-pointer' : 'cursor-default'}`}
                  onClick={() => hasJson && toggle(index)}
                >
                  <span className={`mt-0.5 shrink-0 rounded border px-1.5 py-0.5 font-mono text-[9px] tabular-nums ${toneClasses(theme, meta.tone)}`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className={`font-mono text-[11px] uppercase tracking-widest ${c.text}`}>{item.step}</span>
                      {item.createdAt && <span className={`font-mono text-[10px] ${c.faint}`}>{compactDate(item.createdAt)}</span>}
                    </span>
                    <span className={`mt-1 block text-sm leading-5 ${c.muted}`}>{meta.meaning}</span>
                    {jsonSummary && <span className={`mt-1 block font-mono text-[10px] ${c.faint}`}>{jsonSummary}</span>}
                  </span>
                  {hasJson && (
                    open ? <ChevronDown className={`mt-1 h-3.5 w-3.5 shrink-0 ${c.faint}`} /> : <ChevronRight className={`mt-1 h-3.5 w-3.5 shrink-0 ${c.faint}`} />
                  )}
                </button>
                {open && hasJson && (
                  <div className={`space-y-4 border-t px-3 py-3 ${c.line}`}>
                    {isClassification && item.jsonInfo && (
                      <ClassificationDetails jsonInfo={item.jsonInfo} theme={theme} query={query} />
                    )}
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Raw payload</span>
                        <button
                          type="button"
                          onClick={() => handleCopyJson(index, item.jsonInfo)}
                          className={`flex items-center gap-1 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal ${c.line} ${c.faint}`}
                        >
                          {copiedIndex === index ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          {copiedIndex === index ? 'Copied' : 'Copy JSON'}
                        </button>
                      </div>
                      <pre className={`max-h-72 overflow-auto whitespace-pre-wrap break-words rounded border p-3 font-mono text-[11px] leading-relaxed ${c.line} ${theme === 'dark' ? 'bg-background text-foreground' : 'bg-secondary text-foreground'}`}>
                        {JSON.stringify(item.jsonInfo, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </Widget>
  )
}
